import axios from "axios";

/**
 * CareLogix™ ORACLE — Phase 28 API client (additive).
 *
 *  - Public reasoning endpoints (no auth required for /reason / /agents)
 *  - AdminOne HITL + Briefing Dispatcher endpoints (Bearer token)
 */

const BASE = process.env.REACT_APP_BACKEND_URL;

const ADMIN_TOKEN_KEYS = [
  "carelogix_adminone_token",
  "carelogix_adminone_jwt",
  "adminone_token",
];

function adminToken() {
  for (const k of ADMIN_TOKEN_KEYS) {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(k) : null;
      if (v) return v;
    } catch (_) { /* noop */ }
  }
  return null;
}

const publicHttp = axios.create({ baseURL: `${BASE}/api/oracle/logix2`, timeout: 90_000 });
const adminHttp = axios.create({ baseURL: `${BASE}/api`, timeout: 60_000 });

adminHttp.interceptors.request.use((cfg) => {
  const tok = adminToken();
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export const logix2ReasoningApi = {
  agents: () => publicHttp.get("/agents"),
  reason: ({ symbol, asset_class = "stocks", user_query = "", scanner_type } = {}) =>
    publicHttp.post("/reason", { symbol, asset_class, user_query, scanner_type }),
  reasonGet: (traceId) => publicHttp.get(`/reason/${traceId}`),
  /**
   * Phase 29A — multipart reasoning with optional voice transcript + image.
   *   fd: a FormData with `symbol`, `asset_class`, `user_query`,
   *       optional `voice_transcript`, optional `image` (File/Blob),
   *       optional `scanner_type`.
   */
  reasonWithAttachments: (fd) =>
    publicHttp.post("/reason-with-attachments", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180_000,
    }),
};

export const adminProvidersApi = {
  status: () => adminHttp.get("/adminone/providers/status"),
};

export const adminAnimationApi = {
  get: () => adminHttp.get("/adminone/animation/settings"),
  set: (payload) => adminHttp.post("/adminone/animation/settings", payload || {}),
};

export const publicAnimationApi = {
  get: () => axios.create({ baseURL: `${BASE}/api/oracle`, timeout: 15000 }).get("/animation/settings"),
};

export const logix2HitlApi = {
  queue: (status, limit = 40) =>
    adminHttp.get("/oracle/logix2/hitl/queue", { params: status ? { status, limit } : { limit } }),
  approve: (traceId, note = "") =>
    adminHttp.post(`/oracle/logix2/hitl/${traceId}/approve`, { note }),
  reject: (traceId, note = "") =>
    adminHttp.post(`/oracle/logix2/hitl/${traceId}/reject`, { note }),
  escalate: (traceId, note = "") =>
    adminHttp.post(`/oracle/logix2/hitl/${traceId}/escalate`, { note }),
};

export const briefingDispatcherApi = {
  status: () => adminHttp.get("/adminone/briefing-center/dispatcher/status"),
  history: (limit = 20) =>
    adminHttp.get("/adminone/briefing-center/dispatcher/history", { params: { limit } }),
  preview: () => adminHttp.get("/adminone/briefing-center/dispatcher/preview"),
  dispatchNow: (channel = "both", { smsRecipient, emailRecipient, attachPdf = true } = {}) =>
    adminHttp.post("/adminone/briefing-center/dispatcher/dispatch-now", {
      channel,
      sms_recipient: smsRecipient || null,
      email_recipient: emailRecipient || null,
      attach_pdf: attachPdf,
    }),
  pdfUrl: () => `${BASE}/api/adminone/briefing-center/pdf`,
  fetchPdfBlob: async () => {
    const res = await adminHttp.get("/adminone/briefing-center/pdf", { responseType: "blob" });
    return res.data;
  },
};

export default {
  logix2ReasoningApi,
  logix2HitlApi,
  briefingDispatcherApi,
  adminProvidersApi,
  adminAnimationApi,
  publicAnimationApi,
};
